import { useRoute, createError } from '#app';
import type { Strapi5RequestParams, Strapi5ResponseMany, Strapi5ResponseSingle } from '@nuxtjs/strapi';

/**
 * Validates a draft/preview request.
 *
 * Checks for the presence of the 'draft' query parameter. If present,
 * it verifies the 'secret' query parameter by making a POST request
 * to the '/api/preview' endpoint.
 *
 * Throws a fatal error (403 Forbidden) if the validation fails
 * (e.g., missing secret, invalid secret according to the API).
 *
 * This composable should typically be called before fetching data
 * that depends on the draft status.
 */
export const useDraft = async (fetchConfiguration: Strapi5RequestParams): Promise<Strapi5RequestParams> => {
  const route = useRoute();

  // Only perform validation if the 'draft' query parameter exists
  if (route.query.draft === undefined) {
    return fetchConfiguration; // Not a draft request, nothing to validate so return the original config
  }

  const secret = route.query.secret;

  // Check if the secret is provided when draft mode is requested
  if (!secret) {
    console.warn('Draft mode requested but secret query parameter is missing.');
    throw createError({
      statusCode: 403,
      message: 'Deze actie is niet toegestaan',
      fatal: true,
    });
  }

  // Validate the secret via the API endpoint
  try {
    // Assume the API endpoint '/api/preview' validates the secret.
    // It should throw an error or return a non-successful response if invalid.
    await $fetch('/api/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Ensure the body structure matches what your API expects
      body: { secret: secret },
    });

    // If the fetch is successful (doesn't throw), validation passed.
  } catch (error: any) {
    // Determine appropriate status code and message
    const statusCode = error?.response?.status || 403; // Default to 403 if status is unavailable
    const message = error?.data?.message || // Use message from API error response if available
                    (statusCode === 403 ? 'Deze actie is niet toegestaan' : 'Fout bij validatie preview');

    // Throw a fatal error to stop further execution (e.g., data fetching)
    throw createError({
      statusCode: statusCode,
      message: message,
      fatal: true,
    });
  }

  // Return the fetch configuration with added status 'draft' parameter
  return {
    ...fetchConfiguration,
    //@ts-ignore
    status: 'draft'
  };
};
